/**
 * Sidebar 用のメニューを組み立てる
 * - user / company_employee の2種類でメニューを出し分ける
 * - user / company_employee のときだけ参画プロジェクト（work_histories）を取得する
 */

import { useEffect, useMemo, useState, useCallback } from "react";
import { SIDEBAR_MENUS } from "./sidebarMenus";
import { toast } from "react-toastify";

export const useSidebarMenus = ({ currentUser, currentCompanyUser } = {}) => {
  // 参画プロジェクト一覧（user用）
  const [workHistories, setWorkHistories] = useState([]);

  /**
   * Sidebar を表示するユーザー種別を2択で判定
   * - user
   * - company_employee（CompanyUser かつ role === "employee"）
   */
  const viewerType = useMemo(() => {
    if (currentUser) return "user";
    if (currentCompanyUser?.role === "employee") return "company_employee";
    return null;
  }, [currentUser, currentCompanyUser]);

  // fetch を関数化
  const fetchWorkHistories = useCallback((signal) => {
    return fetch("/api/work_histories", { signal })
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(
            `Failed to fetch work_histories: ${res.status} ${res.statusText}`,
          );
        }
        return res.json();
      })
      .then(setWorkHistories)
      .catch((error) => {
        if (error?.name === "AbortError") return;

        console.error(
          "Failed to fetch work_histories in useSidebarMenus:",
          error,
        );
        setWorkHistories([]);
        toast.error("参画プロジェクトの取得に失敗しました。", {
          toastId: "fetch-error",
        });
      });
  }, []);

  /**
   * 参画プロジェクトを取得
   * - viewerType が有効（user / company_employee）のときに取得する
   * - viewerType が null のときも取得しない
   * - プロジェクトの保存・更新後はイベント経由で再取得する
   */
  useEffect(() => {
    if (!viewerType) {
      setWorkHistories([]);
      return;
    }

    const controller = new AbortController();

    // 初回取得
    fetchWorkHistories(controller.signal);

    // 保存・更新後に Sidebar を最新化するための再取得イベント
    const refreshHandler = () => fetchWorkHistories(controller.signal);
    window.addEventListener("app:refreshSidebarMenus", refreshHandler);

    return () => {
      window.removeEventListener("app:refreshSidebarMenus", refreshHandler);
      controller.abort();
    };
  }, [viewerType, fetchWorkHistories]);

  /**
   * 1) viewerType によるメニュー絞り込み
   * 2) dynamic メニューだけ children を組み立て
   */
  const menus = useMemo(() => {
    // Sidebar 非表示対象なら空配列を返す（保険）
    if (!viewerType) return [];

    // まず表示対象のメニューだけ残す
    const filteredMenus = SIDEBAR_MENUS.filter((menu) =>
      menu.visibleFor?.includes(viewerType),
    );

    // dynamic だけ加工する
    return filteredMenus.map((menu) => {
      // static / action はそのまま返す
      if (menu.type !== "dynamic") return menu;

      // user の参画プロジェクトだけ children を組み立てる
      if (menu.key === "work_histories") {
        return {
          ...menu,
          children: workHistories.map((workHistory) => ({
            id: workHistory.id,

            // 表示用（プロジェクト名）
            label: workHistory.name,

            // 期間表示用（SidebarItem で使う）
            period: workHistory.periodLabel,

            path: `/skill_sheet_mains/${workHistory.id}/edit`,
          })),
        };
      }

      return menu;
    });
  }, [viewerType, workHistories]);

  return menus;
};
